import { Link } from "react-router-dom";
import { ShoppingCart, Eye } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/utils/format";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { trackAddToCart } from "@/utils/gtm";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { ProductVariation } from "@/types/database";

interface ProductCardProps {
  id: string;
  name: string;
  slug: string;
  price: number;
  salePrice?: number | null;
  image: string;
  brand?: string;
  category?: string;
  variations?: ProductVariation[];
}

const ProductCard = ({ id, name, slug, price, salePrice, image, brand, category, variations }: ProductCardProps) => {
  const { addItem, setIsOpen } = useCart();

  // Calculate price display for products with multiple variations
  const hasMultipleVariations = variations && variations.length > 1;
  const minPrice = hasMultipleVariations 
    ? Math.min(...variations.map(v => v.price))
    : (salePrice && salePrice < price ? salePrice : price);
  
  const originalPrice = hasMultipleVariations ? undefined : (salePrice && salePrice < price ? price : undefined);
  const hasDiscount = originalPrice !== undefined;
  const discountPercent = hasDiscount ? Math.round((1 - minPrice / originalPrice) * 100) : 0;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    addItem({
      id: id,
      productId: id,
      productSlug: slug,
      name,
      price: minPrice,
      imageUrl: image,
    });

    // Track add_to_cart event
    trackAddToCart({
      id,
      name,
      price: minPrice,
      brand,
      category,
      quantity: 1,
    });
    
    toast.success("Producto agregado al carrito");
    setIsOpen(true);
  };

  return (
    <Card 
      hover 
      className={cn(
        "group overflow-hidden border bg-card flex flex-col",
        "relative"
      )}
    >
      {/* Discount badge */}
      {hasDiscount && discountPercent > 0 && (
        <div className="absolute top-3 left-3 z-10">
          <Badge variant="destructive" className="animate-badge-pulse">
            -{discountPercent}%
          </Badge>
        </div>
      )}

      {/* Image container */}
      <Link to={`/producto/${slug}`} className="relative overflow-hidden bg-white border-b">
        <OptimizedImage
          src={image}
          alt={`${name} - ${brand || 'Tekton'}`}
          containerClassName="aspect-square"
          className={cn(
            "w-full h-full object-contain p-4",
            "transition-transform duration-300",
            "group-hover:scale-105"
          )}
          useWebP={true}
        />
        
        {/* Hover overlay */}
        <div className={cn(
          "absolute inset-0 bg-primary/80 flex items-center justify-center",
          "opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        )}>
          <Button
            variant="outline-white"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            asChild
          >
            <Link to={`/producto/${slug}`}>
              <Eye className="h-4 w-4 mr-2" />
              Ver detalles
            </Link>
          </Button>
        </div>
      </Link>

      {/* Content */}
      <div className="p-4 space-y-3 flex-1 flex flex-col">
        {brand && (
          <p className="text-xs text-accent font-semibold uppercase tracking-wide">
            {brand}
          </p>
        )}
        
        <Link to={`/producto/${slug}`} className="flex-1">
          <h3 className={cn(
            "font-medium text-foreground line-clamp-2 text-sm",
            "transition-colors duration-200",
            "group-hover:text-primary"
          )}>
            {name}
          </h3>
        </Link>

        {/* Price */}
        <div className="space-y-1">
          {hasMultipleVariations && (
            <span className="text-xs text-muted-foreground">Desde</span>
          )}
          <div className="flex items-baseline gap-2">
            <p className="text-lg font-bold text-primary">
              {formatPrice(minPrice)}
            </p>
            {originalPrice && (
              <p className="text-sm text-muted-foreground line-through">
                {formatPrice(originalPrice)}
              </p>
            )}
          </div>
        </div>

        {/* Add to cart button */}
        <Button
          variant="cta"
          size="sm"
          className="w-full mt-auto"
          onClick={handleAddToCart}
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          Agregar
        </Button>
      </div>
    </Card>
  );
};

export default ProductCard;
